import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Car, Download, ExternalLink, EyeOff, CalendarRange, Pencil } from 'lucide-react'
import { getContentList, exportListUrl } from '../../lib/api/content'
import { RateEditDrawer } from './RateEditDrawer'
import { cn } from '../../lib/cn'

const SEG = [
  { value: '', label: 'Tutte' },
  { value: 'active', label: 'Attive' },
  { value: 'online', label: 'Online' },
]
type El = Record<string, unknown>

const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
function money(c: unknown): string {
  const n = Number(c)
  return Number.isFinite(n) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100) : '—'
}
function durationLabel(hours: unknown): string {
  const h = Number(hours)
  if (!Number.isFinite(h) || h <= 0) return '—'
  if (h < 24) return `${h} ${h === 1 ? 'ora' : 'ore'}`
  if (h % 168 === 0) {
    const w = h / 168
    return `${w} ${w === 1 ? 'settimana' : 'settimane'}`
  }
  if (h % 24 === 0) {
    const d = h / 24
    return `${d} ${d === 1 ? 'giorno' : 'giorni'}`
  }
  return `${h} ore`
}

type TransportInfo = { id: string; label: string }
type RateRow = { id: string; hours: number; price: unknown; originalPrice: unknown; enabled: boolean; online: boolean; priority: number }
type Group = { transport: TransportInfo; rates: RateRow[] }

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="cv-panel px-4 py-3.5">
      <div className="text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{label}</div>
      <div className="mt-1 font-display text-[24px] font-bold leading-none tracking-[-.5px] text-coal">{value}</div>
      {sub && <div className="mt-1 text-[12px] text-muted">{sub}</div>}
    </div>
  )
}

export function RatesPage() {
  const [seg, setSeg] = useState('')
  const [editId, setEditId] = useState<string | null>(null)

  const rates = useQuery({ queryKey: ['rates'], queryFn: () => getContentList('rate', { limit: 500 }) })
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })

  const transportLabel = (id: unknown) => {
    const r = transports.data?.data?.find((t) => String(t.id) === String(id))
    return r ? String((r.elements.name as string) ?? (r.elements.label as string) ?? `Veicolo ${id}`) : `Veicolo ${id}`
  }
  const transportOf = (el: El): TransportInfo => {
    const ref = el.transportId
    if (ref && typeof ref === 'object') {
      const r = ref as { id?: unknown; elements?: El }
      const e = r.elements ?? {}
      const id = String(r.id ?? '')
      return { id, label: String((e.name as string) ?? (e.label as string) ?? transportLabel(id)) }
    }
    return { id: String(ref ?? ''), label: transportLabel(ref) }
  }

  const groups = useMemo<Group[]>(() => {
    const rows = rates.data?.data ?? []
    const map = new Map<string, Group>()
    for (const row of rows) {
      const el = row.elements as El
      const tr = transportOf(el)
      const r: RateRow = {
        id: row.id,
        hours: Number(el.hours) || 0,
        price: el.price,
        originalPrice: el.originalPrice,
        enabled: isYes(el.enabled),
        online: isYes(el.visibleOnFrontEnd),
        priority: Number(el.priority) || 0,
      }
      if (seg === 'active' && !r.enabled) continue
      if (seg === 'online' && !r.online) continue
      let g = map.get(tr.id)
      if (!g) {
        g = { transport: tr, rates: [] }
        map.set(tr.id, g)
      }
      g.rates.push(r)
    }
    const out = [...map.values()]
    out.forEach((g) => g.rates.sort((a, b) => a.hours - b.hours || a.priority - b.priority))
    out.sort((a, b) => Number(a.transport.id) - Number(b.transport.id))
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates.data, transports.data, seg])

  const stats = useMemo(() => {
    const rows = rates.data?.data ?? []
    let active = 0
    let online = 0
    const vehicles = new Set<string>()
    for (const row of rows) {
      const el = row.elements as El
      if (isYes(el.enabled)) active++
      if (isYes(el.visibleOnFrontEnd)) online++
      vehicles.add(transportOf(el).id)
    }
    return { total: rows.length, active, online, vehicles: vehicles.size }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates.data, transports.data])

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
            Gestione tariffe
          </h2>
          <p className="mt-1.5 max-w-[70ch] text-[13.5px] text-muted">
            Listini per tipo di veicolo e durata. <span className="text-coal">Listino</span> = prezzo pieno,{' '}
            <span className="text-coal">Online</span> = importo effettivo di vendita. Clicca una tariffa per modificare
            prezzi, validità e variazioni.
          </p>
        </div>
        <div className="flex w-full items-center gap-2.5 sm:ml-auto sm:w-auto">
          <a
            href="/admin/yep-admin/content/rate/list"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint"
          >
            <ExternalLink className="h-4 w-4" /> Gestionale
          </a>
          <a
            href={exportListUrl('rate')}
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-[10px] text-[13.5px] font-semibold text-white shadow-[0_4px_14px_-6px_rgba(225,2,27,.55)] hover:bg-verdigris-700"
          >
            <Download className="h-4 w-4" /> Esporta Excel
          </a>
        </div>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-2 gap-[14px] lg:grid-cols-4">
        <Stat label="Tariffe totali" value={String(stats.total)} sub="listini configurati" />
        <Stat label="Attive" value={String(stats.active)} sub="in vendita" />
        <Stat label="Visibili online" value={String(stats.online)} sub="mostrate sul sito" />
        <Stat label="Veicoli" value={String(stats.vehicles)} sub="con listino" />
      </div>

      {/* filter */}
      <div className="mb-[18px] inline-flex overflow-hidden rounded-lg border border-line bg-card">
        {SEG.map((s) => (
          <button
            key={s.label}
            onClick={() => setSeg(s.value)}
            className={cn(
              'px-3.5 py-2 text-[13px] font-semibold transition-colors',
              seg === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* per-vehicle listini */}
      {rates.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare le tariffe.
        </div>
      ) : rates.isLoading ? (
        <div className="grid grid-cols-1 gap-[18px] md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="cv-panel p-5">
              <div className="skeleton h-40 w-full rounded bg-line-soft" />
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">Nessuna tariffa trovata.</div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-[18px] md:grid-cols-2 xl:grid-cols-3">
          {groups.map((g) => (
            <div key={g.transport.id} className="cv-panel overflow-hidden">
              <div className="flex items-center gap-2.5 border-b border-line-soft px-4 py-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                  <Car className="h-4 w-4" />
                </span>
                <span className="font-display text-[15px] font-bold text-coal">{g.transport.label}</span>
                <span className="ml-auto rounded-full bg-paper px-2 py-0.5 text-[11px] font-semibold text-muted ring-1 ring-line">
                  {g.rates.length} {g.rates.length === 1 ? 'durata' : 'durate'}
                </span>
              </div>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-line-soft text-[11px] font-bold uppercase tracking-[1px] text-muted">
                    <th className="px-4 py-2 text-left">Durata</th>
                    <th className="px-3 py-2 text-right">Listino</th>
                    <th className="px-4 py-2 text-right">Online</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rates.map((r) => {
                    const orig = Number(r.originalPrice)
                    const sale = Number(r.price)
                    const disc = orig > 0 && sale > 0 && orig > sale ? Math.round((1 - sale / orig) * 100) : 0
                    return (
                      <tr
                        key={r.id}
                        onClick={() => setEditId(r.id)}
                        className={cn('group cursor-pointer border-b border-line-soft last:border-0 hover:bg-paper', !r.enabled && 'opacity-50')}
                      >
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5 font-medium text-coal">
                            {durationLabel(r.hours)}
                            <Pencil className="h-3 w-3 text-faint opacity-0 transition-opacity group-hover:opacity-100" />
                          </span>
                          {!r.online && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[10.5px] text-faint" title="Non visibile sul sito">
                              <EyeOff className="h-3 w-3" />
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono tnum text-muted">
                          {orig > 0 && orig !== sale ? <span className="line-through">{money(r.originalPrice)}</span> : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="font-mono font-semibold tnum text-coal">{money(r.price)}</span>
                          {disc > 0 && <span className="ml-1.5 text-[11px] font-semibold text-ok">−{disc}%</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* seasonal note */}
      <div className="mt-[18px] flex items-start gap-3 rounded-card border border-[#f0e0b0] bg-oro-soft px-4 py-3.5 text-[13px] text-oro-700">
        <CalendarRange className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          Stagionalità e sovrapprezzi puntuali si impostano per ogni tariffa nelle schede <b>Validità</b> e{' '}
          <b>Variazioni</b>. Le stagioni ricorrenti restano nel Calendario tariffe.
        </div>
      </div>

      <RateEditDrawer rateId={editId} onClose={() => setEditId(null)} />
    </div>
  )
}
